import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    MIN_UPDATE_INTERVAL_MS,
    previewShownRoiNumbers,
} from '~/containers/viewer/viewport/previewRoiNumbers';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiContourEditEvent,
    RoiContoursModifiedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import type { Environment } from '~/engine/data/Environment';
import { previewSurfaceColor } from '~/engine/tool/actions/contouring-actions/previewSurfaceColor';
import {
    PreviewVolume,
    previewVolumeOf,
} from '~/engine/tool/actions/contouring-actions/RoiPreviewRenderer';
import { sameElements } from '~/utils/array';

export interface CommittedRoiVolumes {
    /** Ready-to-upload sub-volumes of the shown ROIs, in ROI-number order. */
    volumes: readonly PreviewVolume[];
    /** How many of the selection have something to draw. */
    shownCount: number;
    /** How many of the selection could be drawn at all. */
    drawableCount: number;
    /**
     * Zero until the volumes of a selection have been resolved, then bumped
     * when the selection moved, as opposed to an edit landing on what is shown.
     */
    selectionToken: number;
}

/**
 * The committed structures the 3-D preview draws, as GPU-ready volumes: of
 * `drawableRoiNumbers`, the ROIs of `environment` that have contours, within
 * the preview's slice budget. Kept current, throttled, as those ROIs are
 * edited or recoloured in the structure set `structureSetUid`; with no set
 * named no edit refreshes them.
 */
export function useCommittedRoiVolumes(
    environment: Environment | undefined,
    structureSetUid: string | undefined,
    drawableRoiNumbers: readonly number[]
): CommittedRoiVolumes {
    const [shownRoiNumbers, setShownRoiNumbers] = useState<readonly number[]>(
        []
    );
    const [volumes, setVolumes] = useState<readonly PreviewVolume[]>([]);
    const [selectionToken, setSelectionToken] = useState(0);
    const drawableRoiNumbersRef = useRef(drawableRoiNumbers);
    const environmentRef = useRef(environment);
    const structureSetUidRef = useRef(structureSetUid);
    const shownRoiNumbersRef = useRef<readonly number[]>(shownRoiNumbers);
    // Keyed on what could be drawn, so an ROI with no contours yet keeps the subscription that shows its first stroke.
    const isActive = drawableRoiNumbers.length > 0;
    useEffect(() => {
        drawableRoiNumbersRef.current = drawableRoiNumbers;
        environmentRef.current = environment;
        structureSetUidRef.current = structureSetUid;
    });
    const resolveShown = useCallback((): readonly number[] => {
        const environment = environmentRef.current;
        const next = previewShownRoiNumbers(
            drawableRoiNumbersRef.current,
            (roiNumber) => {
                const texture = environment?.getRoi(roiNumber)?.texture;
                if (texture === undefined || texture.isEmpty) {
                    return null;
                }
                return Math.round(texture.sizePx[2]);
            }
        );
        const previous = shownRoiNumbersRef.current;
        if (sameElements(previous, next)) {
            return previous;
        }
        shownRoiNumbersRef.current = next;
        setShownRoiNumbers(next);
        return next;
    }, []);
    const rebuild = useCallback((): void => {
        const environment = environmentRef.current;
        if (!environment) {
            setVolumes([]);
            return;
        }
        const roiNumbers = resolveShown();
        try {
            const next: PreviewVolume[] = [];
            for (const roiNumber of roiNumbers) {
                const roi = environment.getRoi(roiNumber);
                const texture = roi?.texture;
                if (!roi || !texture || texture.isEmpty) {
                    continue;
                }
                next.push(
                    previewVolumeOf(texture, previewSurfaceColor(roi.color))
                );
            }
            setVolumes(next);
        } catch (error) {
            // Degrades to an empty preview rather than throwing out of an effect or a timer.
            console.warn('RoiPreviewPane: loading the preview failed', error);
            setVolumes([]);
        }
    }, [resolveShown]);
    const throttledRefresh = useMemo(
        () => throttle(rebuild, MIN_UPDATE_INTERVAL_MS),
        [rebuild]
    );
    useEffect(() => {
        if (!isActive) {
            shownRoiNumbersRef.current = [];
            setShownRoiNumbers([]);
            setVolumes([]);
            return;
        }
        rebuild();
        setSelectionToken((token) => token + 1);
    }, [isActive, drawableRoiNumbers, rebuild]);
    useEffect(() => {
        if (!isActive) {
            return;
        }
        const refreshIfShown = (
            event: RoiContourEditEvent | RoiPropertiesUpdatedEvent
        ) => {
            if (
                !drawableRoiNumbersRef.current.includes(event.roiNumber) ||
                (event instanceof RoiPropertiesUpdatedEvent &&
                    event.updatedProperties.color === undefined)
            ) {
                return;
            }
            // ROI numbers are per structure set: only an edit or undo/redo
            // of the named set may refresh. With no set named its edits
            // cannot be told from another set's, so none refreshes.
            const structureSetUid = structureSetUidRef.current;
            if (
                structureSetUid === undefined ||
                event.structureSetUid !== structureSetUid
            ) {
                return;
            }
            throttledRefresh();
        };
        const subscriptions = [
            RoiContoursModifiedEvent.EVENT_TYPE,
            ContourUndoRequestedEvent.EVENT_TYPE,
            ContourRedoRequestedEvent.EVENT_TYPE,
            RoiPropertiesUpdatedEvent.EVENT_TYPE,
        ].map((eventType) =>
            domainEventBus.subscribe(eventType, refreshIfShown)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
            throttledRefresh.cancel();
        };
    }, [isActive, throttledRefresh]);
    return useMemo(
        () => ({
            volumes,
            shownCount: shownRoiNumbers.length,
            drawableCount: drawableRoiNumbers.length,
            selectionToken,
        }),
        [volumes, shownRoiNumbers, drawableRoiNumbers, selectionToken]
    );
}
